import { forwardRef, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

import SvgIcon from '../SvgIcon';
import colors from '@/theme/colors';
import { Button, Text } from '@chakra-ui/react';
import { extra, primary, secondary } from '@/theme/colors/palettes';

type CustomButtonProps = {
  label?: string;
  iconBeforeLabelJSX?: JSX.Element;
  iconBeforeLabelHoveredJSX?: JSX.Element;
  iconBeforeLabelSvgPath?: string;
  iconBeforeLabelSvgColor?: string;
  iconBeforeLabelSvgSize?: number | string;
  iconBeforeLabelSvgBoxWidth?: number | string;
  iconBeforeLabelSvgBoxHeight?: number | string;
  iconAfterLabelSvgBoxWidth?: number | string;
  iconAfterLabelSvgBoxHeight?: number | string;
  iconAfterLabelSvgPath?: string;
  iconAfterLabelSvgColor?: string;
  iconAfterLabelSvgSize?: number | string;
  iconAfterLabelSvgBox?: number | string;
  buttonType?: 'button' | 'submit' | 'reset' | undefined;
  onClick?: () => void;
  disabled?: boolean;
  disabledColor?: string;
  icon?: string;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  labelColor?: string;
  labelHoverColor?: string;
  isTransparent?: boolean;
  width?: string | number;
  height?: string | number;
  borderColor?: string;
  borderHoverColor?: string;
  borderHoverWidth?: string;
  isLoading?: boolean;
  noShadow?: boolean;
  hoverOpacityDrop?: boolean;
  style?: CSSProperties;
  labelStyle?: CSSProperties;
};

const colorToHoverColor: Record<string, string> = {
  'secondary.500': 'secondary.600',
  'primary.500': 'primary.600',
  'primary.400': 'primary.600',
  'extra.white': 'extra.white',
  white: 'extra.white',
  'extra.red': 'extra.darkRed',
};

const colorToBorderHoverColor: Record<string, string> = {
  'secondary.500': secondary[600],
  'primary.500': primary[600],
  'primary.400': primary[600],
  'extra.white': extra.white,
  white: extra.white,
  'extra.red': extra.darkRed,
};

const backgroundColorToDisabledColor: Record<string, string> = {
  'primary.500': 'primary.300',
  'secondary.500': 'secondary.300',
  '': 'background.gray',
};

const boxShadowColor = (backgroundColor: string | undefined) => {
  switch (backgroundColor) {
    case 'secondary.500':
    case 'secondary.600':
      return '#7D3F004D';

    case 'primary.500':
    case 'primary.600':
      return '#1A00B980';

    case 'extra.red':
      return '#7D3F004D';

    default:
      return '#7C9EBC80';
  }
};

const CustomButton = forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      label,
      iconBeforeLabelJSX,
      iconBeforeLabelHoveredJSX,
      iconBeforeLabelSvgPath,
      iconAfterLabelSvgPath,
      buttonType = 'button',
      onClick,
      disabled,
      icon,
      backgroundColor,
      disabledColor = backgroundColorToDisabledColor[backgroundColor ?? ''],
      hoverBackgroundColor = backgroundColor ? colorToHoverColor[backgroundColor] : 'transparent',
      labelColor,
      iconBeforeLabelSvgColor = labelColor ?? colors.background.blueGray,
      iconAfterLabelSvgColor = labelColor ?? colors.background.blueGray,
      iconBeforeLabelSvgSize,
      iconBeforeLabelSvgBoxWidth,
      iconBeforeLabelSvgBoxHeight,
      iconAfterLabelSvgSize,
      iconAfterLabelSvgBoxWidth,
      iconAfterLabelSvgBoxHeight,
      labelHoverColor,
      isTransparent,
      width,
      height = '54px',
      borderColor,
      borderHoverColor = isTransparent
        ? 'transparent'
        : !backgroundColor
          ? primary[500]
          : colorToBorderHoverColor[backgroundColor],
      borderHoverWidth = '2px',
      isLoading = false,
      noShadow = false,
      style,
      labelStyle,
      hoverOpacityDrop,
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    const background = useMemo(() => {
      if (disabled) return disabledColor;
      if (isHovered) return hoverBackgroundColor;
      if (isTransparent) return 'transparent';
      return backgroundColor;
    }, [disabled, disabledColor, isHovered, hoverBackgroundColor, isTransparent, backgroundColor]);

    useEffect(() => {
      if (disabled) {
        setIsHovered(false);
      }
    }, [disabled]);

    return (
      <Button
        background={background}
        tabIndex={-1}
        onMouseEnter={() => (disabled ? null : setIsHovered(true))}
        onMouseLeave={() => setIsHovered(false)}
        onClick={disabled ? () => {} : onClick}
        disabled={disabled || isLoading}
        width={width}
        height={height}
        isLoading={isLoading}
        boxShadow={isTransparent || disabled || noShadow ? '' : `0px 2px 4px 0px ${boxShadowColor(backgroundColor)}`}
        type={disabled ? 'button' : buttonType}
        border={
          isTransparent
            ? '0px'
            : isHovered
              ? `1px solid ${borderHoverColor}`
              : `1px solid ${borderColor ? borderColor : 'transparent'}`
        }
        borderWidth={isHovered ? borderHoverWidth : '2px'}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        style={style}
        ref={ref}
        _hover={{ opacity: hoverOpacityDrop ? 0.7 : 1 }}
      >
        {iconBeforeLabelJSX && ((iconBeforeLabelHoveredJSX && !isHovered) || !iconBeforeLabelHoveredJSX)
          ? iconBeforeLabelJSX
          : null}
        {iconBeforeLabelHoveredJSX && isHovered ? iconBeforeLabelHoveredJSX : null}

        {iconBeforeLabelSvgPath ? (
          <SvgIcon
            iconPath={iconBeforeLabelSvgPath}
            size={iconBeforeLabelSvgSize}
            boxHeight={iconBeforeLabelSvgBoxHeight}
            boxWidth={iconBeforeLabelSvgBoxWidth}
            color={(isHovered ? labelHoverColor : iconBeforeLabelSvgColor) ?? iconBeforeLabelSvgColor}
          />
        ) : null}

        <Text
          color={isHovered ? (labelHoverColor ?? labelColor) : (labelColor ?? 'background.blueGray')}
          marginLeft={icon || iconBeforeLabelJSX || iconBeforeLabelSvgPath ? '10px' : 0}
          marginRight={icon || iconAfterLabelSvgPath ? '10px' : 0}
          variant={'urbanistBold'}
          style={labelStyle}
        >
          {label}
        </Text>

        {iconAfterLabelSvgPath ? (
          <SvgIcon
            iconPath={iconAfterLabelSvgPath}
            size={iconAfterLabelSvgSize}
            boxHeight={iconAfterLabelSvgBoxHeight}
            boxWidth={iconAfterLabelSvgBoxWidth}
            color={(isHovered ? labelHoverColor : iconAfterLabelSvgColor) ?? iconAfterLabelSvgColor}
          />
        ) : null}
      </Button>
    );
  },
);

export default CustomButton;
